import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Drawer, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { ProjectDataProvider, useProjectData } from '../ProjectOverview/ProjectDataContext';
import NotesPanel from '../ProjectOverview/panels/NotesPanel';
import DeliverablesPanel from '../ProjectOverview/panels/DeliverablesPanel';
import DeliverableDetailDialog from '../ProjectOverview/parts/DeliverableDetailDialog';
import { KlantbriefingDropzone, MailKoppelBox } from '../ProjectFlow/bronnenKoppelen';

/**
 * Projectstrook op de callsheet (Joris 28 sep): welk project, welke bronnen de AI
 * straks leest, en snel iets toevoegen zonder de callsheet te verlaten.
 *
 *   Project #9820 · Schrijf-Schrijf — Impact in 60 seconden        [↗ Projectpagina]
 *   Bronnen voor AI:  ✓ 1 briefing   ✓ 3 notities   ⚠ 0 mails   ✓ dossier   ⚠ 0 meetings
 *   [+ Briefing]  [+ Mail koppelen]  [+ Notitie]  [Offerte openen]  [Deliverables]
 *
 * De telling komt van de engine (callsheet_briefing.tel_bronnen) en gebruikt exact
 * dezelfde verzamelaars als "Voorbereiden met AI".
 */

const ORANJE = '#b45309';

// Volgorde en uitleg per soort — sleutels zoals de engine ze teruggeeft.
export const BRON_SOORTEN = [
  { key: 'briefings', enkel: 'briefing', meer: 'briefings',
    uitleg: 'Wat de klant zelf over de opname schreef. De AI leest dit als eerste; zonder briefing mist vaak de locatie of het doel. Toevoegen via + Briefing.' },
  { key: 'notities', enkel: 'notitie', meer: 'notities',
    uitleg: 'Projectnotities, ook gekoppelde mails en OneNote-pagina\'s. Hier staan vaak de afspraken over tijden, locatie en wie er is.' },
  { key: 'mails', enkel: 'mail', meer: 'mails',
    uitleg: 'Mails in jóuw mailbox met het projectnummer of de klantnaam (laatste 180 dagen). Staat de mail ergens anders, koppel hem dan via + Mail koppelen.' },
  { key: 'dossier', enkel: 'dossier', meer: 'dossier', zonderAantal: true,
    uitleg: 'Projectdossier en deliverables: doel, doelgroep en wat er opgeleverd wordt. Daarmee weet de crew welke shots en formaten nodig zijn.' },
  { key: 'meetings', enkel: 'meeting', meer: 'meetings',
    uitleg: 'Gekoppelde meetings (samenvatting of transcript) waar jij bij was. Koppel ze op de projectpagina onder Meetings.' },
];

const PANELEN = { briefing: '+ Briefing', mail: 'Mail koppelen', notitie: 'Notities', deliverables: 'Deliverables' };

// "9820. Schrijf-Schrijf - Impact in 60 seconden" → "Schrijf-Schrijf — Impact in 60 seconden"
export function kaleProjectnaam(naam, nummer) {
  let n = String(naam || '').trim();
  if (nummer) n = n.replace(new RegExp(`^\\s*#?${nummer}\\s*[.:\\-–]?\\s*`), '');
  return n.replace(/\s+-\s+/, ' — ');
}

function Bron({ soort, aantal }) {
  const ok = aantal > 0;
  const tekst = soort.zonderAantal ? soort.enkel : `${aantal} ${aantal === 1 ? soort.enkel : soort.meer}`;
  return (
    <Tooltip title={soort.uitleg} arrow>
      <Typography component="span" variant="body2" data-testid={`bron-${soort.key}`} data-ok={ok ? 'ja' : 'nee'}
        sx={{ color: ok ? 'text.secondary' : ORANJE, fontWeight: ok ? 400 : 600, cursor: 'help', whiteSpace: 'nowrap' }}>
        {ok ? '✓' : '⚠'} {soort.zonderAantal && !ok ? `0 ${soort.enkel}` : tekst}
      </Typography>
    </Tooltip>
  );
}

// Deliverables in het zijpaneel: zelfde paneel als de projectpagina, detail in dezelfde dialoog.
function DeliverablesInZijpaneel({ projectUrl }) {
  const { setDeliverables, tasksPerDeliverable } = useProjectData();
  const [gekozen, setGekozen] = useState(null);
  return (
    <>
      <DeliverablesPanel onOpenDetail={setGekozen}
        onGenerateTasks={() => window.open(`${projectUrl}?paneel=deliverables`, '_blank', 'noopener')} />
      <DeliverableDetailDialog open={!!gekozen} onClose={() => setGekozen(null)} deliverable={gekozen}
        taskCount={gekozen ? (tasksPerDeliverable[gekozen.id] || 0) : 0}
        onGenerateTasks={() => window.open(`${projectUrl}?paneel=deliverables`, '_blank', 'noopener')}
        onUpdate={(bijgewerkt) => {
          setDeliverables((prev) => prev.map((d) => (d.id === bijgewerkt.id ? bijgewerkt : d)));
          setGekozen(bijgewerkt);
        }} />
    </>
  );
}

// Kopknop "↗ Project openen" (naast Terug/Dupliceren/Verwijder): projectpagina in een nieuw tabblad.
export function ProjectOpenenKnop({ projectId }) {
  if (!projectId) return null;
  return (
    <Button variant="outlined" size="small" href={`/projects/${projectId}`} target="_blank" rel="noopener"
      sx={{ textTransform: 'none', fontWeight: 700 }}>
      ↗ Project openen
    </Button>
  );
}

function ProjectStrook({ callsheetId, projectId, projectnummer, projectnaam, klant, onOpnieuwAi, aiBezig, inline = false }) {
  const [aantallen, setAantallen] = useState(null);
  const [laden, setLaden] = useState(false);
  const [fout, setFout] = useState(false);
  const [paneel, setPaneel] = useState(null);
  const [nieuweBron, setNieuweBron] = useState(false);
  const vorigeRef = useRef(null);
  const heeftProject = !!(projectId || projectnummer);

  const tel = useCallback(async () => {
    if (!heeftProject) return;
    setLaden(true); setFout(false);
    try {
      // Het project leidt de server zelf af uit de callsheet (Codex r1); geen projectparameters meesturen.
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${encodeURIComponent(callsheetId)}/bronnen`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const nieuw = (await r.json())?.aantallen || {};
      const vorige = vorigeRef.current;
      if (vorige && BRON_SOORTEN.some(({ key }) => (nieuw[key] || 0) > (vorige[key] || 0))) setNieuweBron(true);
      vorigeRef.current = nieuw;
      setAantallen(nieuw);
    } catch { setFout(true); }
    finally { setLaden(false); }
  }, [callsheetId, heeftProject]);

  useEffect(() => { vorigeRef.current = null; setNieuweBron(false); tel(); }, [tel]);

  const sluitPaneel = () => { setPaneel(null); tel(); };
  const projectUrl = projectId ? `/projects/${projectId}` : null;
  const naam = kaleProjectnaam(projectnaam, projectnummer);
  // In de kop rustig grijs: kleur is voor wat ontbreekt en de primaire knop (Joris 28 sep).
  const knopSx = inline ? { textTransform: 'none', minWidth: 0, color: '#475569' } : { textTransform: 'none', minWidth: 0 };

  if (!heeftProject && inline) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testid="projectstrook">
        Nog geen project gekoppeld — de AI heeft dan geen bronnen.{' '}
        <Box component="a" href="/draaidagen" target="_blank" rel="noopener" sx={{ color: 'inherit' }}>↗ Draaidagen</Box>
      </Typography>
    );
  }

  if (!heeftProject) {
    return (
      <Paper sx={{ px: 2.5, py: 1.5, mb: 3, border: '1px solid #e5e7eb' }} data-testid="projectstrook">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>Nog geen project gekoppeld</Typography>
          {/* Ruimte voor de knop "Project koppelen" (wordt apart gebouwd). */}
          <Button size="small" href="/draaidagen" target="_blank" rel="noopener" sx={knopSx}>↗ Draaidagen</Button>
        </Box>
      </Paper>
    );
  }

  const bronnenEl = (
    <>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>Bronnen voor AI:</Typography>
      {aantallen && BRON_SOORTEN.map((s) => <Bron key={s.key} soort={s} aantal={aantallen[s.key] || 0} />)}
      {laden && <CircularProgress size={12} />}
      {fout && !laden && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          tellen lukte niet — <Box component="span" onClick={tel} sx={{ textDecoration: 'underline', cursor: 'pointer' }}>opnieuw</Box>
        </Typography>
      )}
    </>
  );
  const knopVariant = inline ? 'text' : 'outlined';
  const knoppenEl = (
    <>
      {projectId && <Button size="small" variant={knopVariant} sx={knopSx} onClick={() => setPaneel('briefing')}>+ Briefing</Button>}
      {projectId && <Button size="small" variant={knopVariant} sx={knopSx} onClick={() => setPaneel('mail')}>+ Mail koppelen</Button>}
      {projectId && <Button size="small" variant={knopVariant} sx={knopSx} onClick={() => setPaneel('notitie')}>+ Notitie</Button>}
      {projectnummer && (
        <Button size="small" sx={knopSx} href={`/offertes?mode=new&number=${encodeURIComponent(projectnummer)}`} target="_blank" rel="noopener">
          Offerte openen
        </Button>
      )}
      {projectId && <Button size="small" sx={knopSx} onClick={() => setPaneel('deliverables')}>Deliverables</Button>}
    </>
  );
  const nieuweBronEl = (
    <>
      {/* 4. na een nieuwe bron: opnieuw voorbereiden */}
      {nieuweBron && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1.25, pt: 1.25, borderTop: '1px solid #eef0f3' }}
          data-testid="nieuwe-bron">
          <Typography variant="body2">Nieuwe bron toegevoegd</Typography>
          <Button size="small" variant="outlined" sx={knopSx} disabled={aiBezig}
            onClick={() => { setNieuweBron(false); onOpnieuwAi?.(); }}>
            {aiBezig ? 'AI leest het project…' : 'Opnieuw voorbereiden met AI'}
          </Button>
          <Button size="small" sx={{ ...knopSx, color: 'text.secondary' }} onClick={() => setNieuweBron(false)}>Later</Button>
        </Box>
      )}
    </>
  );
  const drawerEl = (
    <Drawer anchor="right" open={!!paneel} onClose={sluitPaneel}
      PaperProps={{ sx: { width: { xs: '100vw', md: 720 }, maxWidth: '96vw', p: { xs: 2, md: 3 } } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, flex: 1 }}>
          {PANELEN[paneel]}{projectnummer ? ` · #${projectnummer}` : ''}
        </Typography>
        <IconButton size="small" onClick={sluitPaneel} aria-label="Sluiten"><CloseIcon fontSize="small" /></IconButton>
      </Box>
      {paneel === 'briefing' && (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            De briefing wordt bij het project bewaard en geanalyseerd; de AI leest hem daarna als eerste bron.
          </Typography>
          <KlantbriefingDropzone projectRecId={projectId} onBewaard={sluitPaneel} />
        </>
      )}
      {paneel === 'mail' && <MailKoppelBox projectRecId={projectId} projectNumber={projectnummer || ''} projectName={projectnaam || ''} />}
      {paneel === 'notitie' && (
        <ProjectDataProvider projectId={projectId}><NotesPanel /></ProjectDataProvider>
      )}
      {paneel === 'deliverables' && (
        <ProjectDataProvider projectId={projectId}><DeliverablesInZijpaneel projectUrl={projectUrl} /></ProjectDataProvider>
      )}
    </Drawer>
  );

  if (inline) {
    // Compact als regel in de callsheet-kop (Joris 28 sep): geen eigen kaart, geen projectkop
    // (die staat al in de kop), bronnen en knoppen op één regel.
    return (
      <Box data-testid="projectstrook">
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1.5, rowGap: 0.25, flexWrap: 'wrap' }}>
          {bronnenEl}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', ml: { md: 'auto' } }}>{knoppenEl}</Box>
        </Box>
        {nieuweBronEl}
        {drawerEl}
      </Box>
    );
  }

  return (
    <Paper sx={{ px: 2.5, py: 1.75, mb: 3, border: '1px solid #e5e7eb' }} data-testid="projectstrook">
      {/* 1. projectkop */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ fontWeight: 700 }}>Project {projectnummer ? `#${projectnummer}` : ''}</Box>
          {naam ? ` · ${naam}` : ''}
        </Typography>
        {projectUrl && (
          <Button size="small" href={projectUrl} target="_blank" rel="noopener" sx={knopSx}>↗ Projectpagina</Button>
        )}
      </Box>

      {/* 2. bronnen */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 0.75 }}>
        {bronnenEl}
      </Box>

      {/* 3. knoppen */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
        {knoppenEl}
      </Box>

      {nieuweBronEl}

      {drawerEl}
    </Paper>
  );
}

export default ProjectStrook;
